import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import userService from '../../services/userService';
import { authService } from '../../services/authService';
import { getWarehouses } from '../../services/warehousesService';
import Toast from '../../components/Common/Toast';
import ConfirmModal from '../../components/Common/ConfirmModal';
import LimitedUserAccessFields from '../../components/Admin/LimitedUserAccessFields';
import { getPointOfSales } from '../../services/pointOfSalesService';
import { getPurchaseSites } from '../../services/purchaseSitesService';
import {
  canSuperAdminAssignContabilidadAccess,
  canUserAccessContabilidad,
} from '../../utils/contabilidadAccess';

const defaultFormData = () => ({
  name: '',
  email: '',
  password: '',
  password_confirmation: '',
  role: 'usuario',
  is_active: true,
  warehouse_ids: [],
  point_of_sale_ids: [],
  purchase_site_ids: [],
  access_machinery: false,
  access_produccion_concesionaria: false,
  access_produccion: false,
  access_restaurantes: false,
  access_contabilidad: false,
});

const defaultResetPasswordData = () => ({
  password: '',
  password_confirmation: '',
});

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isToggleStatusModalOpen, setIsToggleStatusModalOpen] = useState(false);
  const [isResetPasswordModalOpen, setIsResetPasswordModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState(defaultFormData());
  const [resetPasswordData, setResetPasswordData] = useState(defaultResetPasswordData());
  const [resetPasswordErrors, setResetPasswordErrors] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [pointOfSaleOptions, setPointOfSaleOptions] = useState([]);
  const [purchaseSiteOptions, setPurchaseSiteOptions] = useState([]);
  const [currentUser, setCurrentUser] = useState(() => authService.getCurrentUser());

  const isSuperAdmin = Boolean(currentUser?.is_super_admin);

  useEffect(() => {
    loadUsers();
    authService.syncUserFromApi()
      .then((user) => setCurrentUser(user))
      .catch(() => setCurrentUser(authService.getCurrentUser()));
  }, []);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await userService.getUsers();
      if (response.success) {
        setUsers(Array.isArray(response.data) ? response.data : []);
      } else {
        setToast({
          isOpen: true,
          message: response.message || 'Error al cargar usuarios',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cargar usuarios',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const loadWarehouses = async () => {
    try {
      const res = await getWarehouses();
      const list = res?.data ?? res;
      setWarehouseOptions(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error('Error al cargar almacenes:', e);
      setWarehouseOptions([]);
    }
  };

  const loadPointOfSales = async () => {
    try {
      const res = await getPointOfSales();
      const list = res?.data ?? res;
      setPointOfSaleOptions(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error('Error al cargar puntos de venta:', e);
      setPointOfSaleOptions([]);
    }
  };

  const loadPurchaseSites = async () => {
    try {
      const res = await getPurchaseSites({ active_only: true });
      const list = res?.data ?? res;
      setPurchaseSiteOptions(Array.isArray(list) ? list : []);
    } catch (e) {
      console.error('Error al cargar sedes de compra:', e);
      setPurchaseSiteOptions([]);
    }
  };

  const loadLimitedAccessOptions = async () => {
    await Promise.all([loadWarehouses(), loadPointOfSales(), loadPurchaseSites()]);
  };

  const handleOpenCreateModal = async () => {
    setSelectedUser(null);
    setFormData(defaultFormData());
    setFormErrors({});
    await loadLimitedAccessOptions();
    setIsModalOpen(true);
  };

  const handleOpenEditModal = async (user) => {
    setSelectedUser(user);
    const access = user.module_access || {};
    setFormData({
      role: user.role,
      warehouse_ids: Array.isArray(user.warehouse_ids) ? [...user.warehouse_ids] : [],
      point_of_sale_ids: Array.isArray(user.point_of_sale_ids) ? [...user.point_of_sale_ids] : [],
      purchase_site_ids: Array.isArray(user.purchase_site_ids) ? [...user.purchase_site_ids] : [],
      access_machinery: Boolean(access.machinery),
      access_produccion_concesionaria: Boolean(access.produccion_concesionaria),
      access_produccion: Boolean(access.produccion),
      access_restaurantes: Boolean(access.restaurantes),
      access_contabilidad: Boolean(user.access_contabilidad),
    });
    setFormErrors({});
    await loadLimitedAccessOptions();
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
    setFormData(defaultFormData());
    setFormErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const errorMsg = (field) => {
    const v = formErrors[field];
    return v == null ? '' : Array.isArray(v) ? v[0] : String(v);
  };

  const handleWarehouseToggle = (warehouseId) => {
    setFormData(prev => {
      const ids = prev.warehouse_ids || [];
      const next = ids.includes(warehouseId)
        ? ids.filter(id => id !== warehouseId)
        : [...ids, warehouseId];
      return { ...prev, warehouse_ids: next };
    });
  };

  const handlePointOfSaleToggle = (pointOfSaleId) => {
    setFormData((prev) => {
      const ids = prev.point_of_sale_ids || [];
      const next = ids.includes(pointOfSaleId)
        ? ids.filter((id) => id !== pointOfSaleId)
        : [...ids, pointOfSaleId];
      return { ...prev, point_of_sale_ids: next };
    });
  };

  const handlePurchaseSiteToggle = (purchaseSiteId) => {
    setFormData((prev) => {
      const ids = prev.purchase_site_ids || [];
      const next = ids.includes(purchaseSiteId)
        ? ids.filter((id) => id !== purchaseSiteId)
        : [...ids, purchaseSiteId];
      return { ...prev, purchase_site_ids: next };
    });
  };

  const handleModuleAccessToggle = (field) => {
    setFormData((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const appendLimitedAccessToPayload = (payload) => {
    let next = { ...payload };
    if (formData.role === 'limitado') {
      next = {
        ...next,
        warehouse_ids: formData.warehouse_ids || [],
        point_of_sale_ids: formData.point_of_sale_ids || [],
        purchase_site_ids: formData.purchase_site_ids || [],
        access_machinery: Boolean(formData.access_machinery),
        access_produccion_concesionaria: Boolean(formData.access_produccion_concesionaria),
        access_produccion: Boolean(formData.access_produccion),
        access_restaurantes: Boolean(formData.access_restaurantes),
      };
    }
    if (isSuperAdmin) {
      next.access_contabilidad = Boolean(formData.access_contabilidad);
    }
    return next;
  };

  const buildUserUpdatePayload = (user, overrides = {}) => {
    const payload = {
      name: user.name,
      email: user.email,
      role: user.role,
      is_active: user.is_active,
      ...overrides,
    };

    if (user.role === 'limitado') {
      if (Array.isArray(user.warehouse_ids)) {
        payload.warehouse_ids = user.warehouse_ids;
      }
      if (Array.isArray(user.point_of_sale_ids)) {
        payload.point_of_sale_ids = user.point_of_sale_ids;
      }
      if (Array.isArray(user.purchase_site_ids)) {
        payload.purchase_site_ids = user.purchase_site_ids;
      }
      const access = user.module_access || {};
      payload.access_machinery = Boolean(access.machinery);
      payload.access_produccion_concesionaria = Boolean(access.produccion_concesionaria);
      payload.access_produccion = Boolean(access.produccion);
      payload.access_restaurantes = Boolean(access.restaurantes);
    }

    if (isSuperAdmin) {
      payload.access_contabilidad = overrides.access_contabilidad !== undefined
        ? Boolean(overrides.access_contabilidad)
        : Boolean(user.access_contabilidad);
    }

    return payload;
  };

  const handleContabilidadAccessToggle = async (user, nextValue) => {
    if (!isSuperAdmin || !canSuperAdminAssignContabilidadAccess(user)) {
      return;
    }

    try {
      const response = await userService.updateUser(
        user.id,
        buildUserUpdatePayload(user, { access_contabilidad: nextValue })
      );
      if (response.success) {
        setToast({
          isOpen: true,
          message: nextValue
            ? `${user.name} ahora puede ver Contabilidad`
            : `Se retiró el acceso a Contabilidad de ${user.name}`,
          type: 'success',
        });
        loadUsers();
      }
    } catch (error) {
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al actualizar acceso a Contabilidad',
        type: 'error',
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormErrors({});

    try {
      if (selectedUser) {
        const payload = {
          name: selectedUser.name,
          email: selectedUser.email,
          role: formData.role,
          is_active: selectedUser.is_active,
        };
        const response = await userService.updateUser(
          selectedUser.id,
          appendLimitedAccessToPayload(payload)
        );
        if (response.success) {
          setToast({
            isOpen: true,
            message:
              formData.role === 'limitado'
                ? 'Rol actualizado. El usuario limitado verá los cambios al recargar la página o al volver a iniciar sesión.'
                : 'Rol actualizado exitosamente',
            type: 'success',
          });
          handleCloseModal();
          loadUsers();
        }
      } else {
        const createPayload = appendLimitedAccessToPayload({ ...formData });
        if (createPayload.role !== 'limitado') {
          delete createPayload.warehouse_ids;
          delete createPayload.point_of_sale_ids;
          delete createPayload.access_machinery;
          delete createPayload.access_produccion_concesionaria;
          delete createPayload.access_produccion;
          delete createPayload.access_restaurantes;
        }
        const response = await userService.createUser(createPayload);
        if (response.success) {
          setToast({
            isOpen: true,
            message: 'Usuario creado exitosamente',
            type: 'success'
          });
          handleCloseModal();
          loadUsers();
        }
      }
    } catch (error) {
      console.error('Error al guardar usuario:', error);
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors);
      }
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al guardar usuario',
        type: 'error'
      });
    }
  };

  const handleOpenResetPasswordModal = (user) => {
    setSelectedUser(user);
    setResetPasswordData(defaultResetPasswordData());
    setResetPasswordErrors({});
    setIsResetPasswordModalOpen(true);
  };

  const handleCloseResetPasswordModal = () => {
    setIsResetPasswordModalOpen(false);
    setSelectedUser(null);
    setResetPasswordData(defaultResetPasswordData());
    setResetPasswordErrors({});
  };

  const handleResetPasswordChange = (e) => {
    const { name, value } = e.target;
    setResetPasswordData((prev) => ({ ...prev, [name]: value }));
    if (resetPasswordErrors[name]) {
      setResetPasswordErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    setResetPasswordErrors({});

    try {
      const response = await userService.resetPassword(selectedUser.id, resetPasswordData);
      if (response.success) {
        setToast({
          isOpen: true,
          message: `Contraseña de ${selectedUser.name} restablecida exitosamente`,
          type: 'success',
        });
        handleCloseResetPasswordModal();
      }
    } catch (error) {
      if (error.response?.data?.errors) {
        setResetPasswordErrors(error.response.data.errors);
      }
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al restablecer contraseña',
        type: 'error',
      });
    }
  };

  const handleToggleStatusClick = (user) => {
    setSelectedUser(user);
    setIsToggleStatusModalOpen(true);
  };

  const handleToggleStatusConfirm = async () => {
    try {
      const newStatus = !selectedUser.is_active;
      const response = await userService.updateUser(
        selectedUser.id,
        buildUserUpdatePayload(selectedUser, { is_active: newStatus })
      );
      if (response.success) {
        setToast({
          isOpen: true,
          message: `Usuario ${newStatus ? 'activado' : 'desactivado'} exitosamente`,
          type: 'success'
        });
        setIsToggleStatusModalOpen(false);
        setSelectedUser(null);
        loadUsers();
      }
    } catch (error) {
      console.error('Error al cambiar estado del usuario:', error);
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cambiar estado del usuario',
        type: 'error'
      });
      setIsToggleStatusModalOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="p-6">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="mb-6">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h1 className="text-3xl font-bold text-gray-800">Usuarios</h1>
                <p className="text-gray-600 mt-1">Gestión de usuarios del sistema</p>
              </div>
              <button
                onClick={handleOpenCreateModal}
                className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors"
              >
                + Nuevo Usuario
              </button>
            </div>

            {/* Breadcrumb */}
            <nav className="text-sm text-gray-600 mb-4">
              <Link to="/dashboard" className="hover:text-teal-600">Dashboard</Link>
              <span className="mx-2">/</span>
              <Link to="/admin" className="hover:text-teal-600">Administrador</Link>
              <span className="mx-2">/</span>
              <span className="text-gray-800">Usuarios</span>
            </nav>
          </div>

          {/* Tabla de Usuarios */}
          {loading ? (
            <div className="bg-white rounded-lg shadow-md p-12 text-center">
              <p className="text-gray-500">Cargando usuarios...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-12 text-center">
              <p className="text-gray-500 mb-4">No hay usuarios registrados</p>
              <button
                onClick={handleOpenCreateModal}
                className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors"
              >
                + Crear Primer Usuario
              </button>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-md overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nombre
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rol
                    </th>
                    {isSuperAdmin && (
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contabilidad
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fecha de Creación
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap min-w-[220px]">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{user.name}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{user.email}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full shrink-0 ${
                            user.role === 'administrador'
                              ? 'bg-purple-100 text-purple-800'
                              : user.role === 'contador'
                                ? 'bg-emerald-100 text-emerald-800'
                                : user.role === 'limitado'
                                  ? 'bg-amber-100 text-amber-800'
                                  : 'bg-blue-100 text-blue-800'
                          }`}>
                            {user.role === 'administrador'
                              ? 'Administrador'
                              : user.role === 'contador'
                                ? 'Contador'
                                : user.role === 'limitado'
                                  ? 'Limitado'
                                  : 'Usuario'}
                            {user.role === 'limitado' && Array.isArray(user.warehouse_ids) && user.warehouse_ids.length > 0 && (
                              <span className="opacity-90">· {user.warehouse_ids.length} alm.</span>
                            )}
                            {user.role === 'limitado' && Array.isArray(user.point_of_sale_ids) && user.point_of_sale_ids.length > 0 && (
                              <span className="opacity-90">· {user.point_of_sale_ids.length} POS</span>
                            )}
                          </span>
                        </div>
                      </td>
                      {isSuperAdmin && (
                        <td className="px-6 py-4 whitespace-nowrap">
                          {canSuperAdminAssignContabilidadAccess(user) ? (
                            <label className="inline-flex items-center gap-2 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={Boolean(user.access_contabilidad)}
                                onChange={(e) => handleContabilidadAccessToggle(user, e.target.checked)}
                                className="h-4 w-4 text-teal-600 rounded border-gray-300 focus:ring-teal-500"
                              />
                              <span className="text-sm text-gray-700">Ver módulo</span>
                            </label>
                          ) : (
                            <span className="text-xs text-gray-500">
                              {user.is_super_admin
                                ? 'Super admin'
                                : user.role === 'contador'
                                  ? 'Por rol'
                                  : canUserAccessContabilidad(user)
                                    ? 'Sí'
                                    : 'No'}
                            </span>
                          )}
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          user.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {user.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {new Date(user.created_at).toLocaleDateString('es-PE', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm font-medium whitespace-nowrap">
                        <div className="inline-flex items-center gap-1 flex-nowrap">
                          <button
                            type="button"
                            onClick={() => handleOpenEditModal(user)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-purple-600 hover:bg-purple-50 hover:text-purple-800 transition-colors shrink-0"
                            title="Editar rol y almacenes"
                          >
                            <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            <span>Rol</span>
                          </button>
                          {isSuperAdmin && user.id !== currentUser?.id && (
                            <>
                              <span className="text-gray-300 select-none" aria-hidden="true">|</span>
                              <button
                                type="button"
                                onClick={() => handleOpenResetPasswordModal(user)}
                                className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium text-teal-600 hover:bg-teal-50 hover:text-teal-800 transition-colors shrink-0"
                                title="Restablecer contraseña"
                              >
                                <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
                                </svg>
                                <span>Clave</span>
                              </button>
                            </>
                          )}
                          <span className="text-gray-300 select-none" aria-hidden="true">|</span>
                          <button
                            type="button"
                            onClick={() => handleToggleStatusClick(user)}
                            className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors shrink-0 ${
                              user.is_active
                                ? 'text-amber-600 hover:bg-amber-50 hover:text-amber-800'
                                : 'text-green-600 hover:bg-green-50 hover:text-green-800'
                            }`}
                            title={user.is_active ? 'Desactivar usuario' : 'Activar usuario'}
                          >
                            {user.is_active ? (
                              <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                              </svg>
                            ) : (
                              <svg className="w-3.5 h-3.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                            )}
                            <span>{user.is_active ? 'Desactivar' : 'Activar'}</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Crear/Editar Usuario */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">
                {selectedUser ? 'Cambiar Rol' : 'Nuevo Usuario'}
              </h2>
              <form onSubmit={handleSubmit}>
                {selectedUser ? (
                  // Solo mostrar el campo de rol al editar
                  <>
                    <div className="mb-4">
                      <p className="text-sm text-gray-600 mb-2">
                        Usuario: <span className="font-medium">{selectedUser.name}</span> ({selectedUser.email})
                      </p>
                    </div>
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Rol <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="role"
                        value={formData.role}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                          formErrors.role ? 'border-red-500' : 'border-gray-300'
                        }`}
                        required
                      >
                        <option value="usuario">Usuario</option>
                        <option value="administrador">Administrador</option>
                        <option value="contador">Contador</option>
                        <option value="limitado">Limitado</option>
                      </select>
                      {formErrors.role && errorMsg('role') && (
                        <p className="mt-1 text-sm text-red-600">{errorMsg('role')}</p>
                      )}
                    </div>
                    {formData.role === 'limitado' && (
                      <LimitedUserAccessFields
                        formData={formData}
                        warehouseOptions={warehouseOptions}
                        onWarehouseToggle={handleWarehouseToggle}
                        pointOfSaleOptions={pointOfSaleOptions}
                        onPointOfSaleToggle={handlePointOfSaleToggle}
                        purchaseSiteOptions={purchaseSiteOptions}
                        onPurchaseSiteToggle={handlePurchaseSiteToggle}
                        onModuleAccessToggle={handleModuleAccessToggle}
                      />
                    )}
                    {isSuperAdmin && formData.role !== 'contador' && !selectedUser?.is_super_admin && (
                      <div className="mb-6">
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={Boolean(formData.access_contabilidad)}
                            onChange={() => handleModuleAccessToggle('access_contabilidad')}
                            className="h-4 w-4 text-teal-600 rounded border-gray-300 focus:ring-teal-500"
                          />
                          <span className="text-sm text-gray-700">Puede ver el módulo de Contabilidad</span>
                        </label>
                      </div>
                    )}
                  </>
                ) : (
                  // Mostrar todos los campos al crear
                  <>
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Nombre <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                          formErrors.name ? 'border-red-500' : 'border-gray-300'
                        }`}
                        required
                      />
                      {formErrors.name && errorMsg('name') && (
                        <p className="mt-1 text-sm text-red-600">{errorMsg('name')}</p>
                      )}
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                          formErrors.email ? 'border-red-500' : 'border-gray-300'
                        }`}
                        required
                      />
                      {formErrors.email && errorMsg('email') && (
                        <p className="mt-1 text-sm text-red-600">{errorMsg('email')}</p>
                      )}
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Contraseña <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                          formErrors.password ? 'border-red-500' : 'border-gray-300'
                        }`}
                        required
                        minLength={8}
                      />
                      {formErrors.password && errorMsg('password') && (
                        <p className="mt-1 text-sm text-red-600">{errorMsg('password')}</p>
                      )}
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Confirmar Contraseña <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="password"
                        name="password_confirmation"
                        value={formData.password_confirmation}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                          formErrors.password_confirmation ? 'border-red-500' : 'border-gray-300'
                        }`}
                        required
                        minLength={8}
                      />
                      {formErrors.password_confirmation && errorMsg('password_confirmation') && (
                        <p className="mt-1 text-sm text-red-600">{errorMsg('password_confirmation')}</p>
                      )}
                    </div>

                    <div className="mb-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Rol <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="role"
                        value={formData.role}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                          formErrors.role ? 'border-red-500' : 'border-gray-300'
                        }`}
                        required
                      >
                        <option value="usuario">Usuario</option>
                        <option value="administrador">Administrador</option>
                        <option value="contador">Contador</option>
                        <option value="limitado">Limitado</option>
                      </select>
                      {formErrors.role && errorMsg('role') && (
                        <p className="mt-1 text-sm text-red-600">{errorMsg('role')}</p>
                      )}
                    </div>
                    {formData.role === 'limitado' && (
                      <LimitedUserAccessFields
                        formData={formData}
                        warehouseOptions={warehouseOptions}
                        onWarehouseToggle={handleWarehouseToggle}
                        pointOfSaleOptions={pointOfSaleOptions}
                        onPointOfSaleToggle={handlePointOfSaleToggle}
                        purchaseSiteOptions={purchaseSiteOptions}
                        onPurchaseSiteToggle={handlePurchaseSiteToggle}
                        onModuleAccessToggle={handleModuleAccessToggle}
                      />
                    )}
                    {isSuperAdmin && formData.role !== 'contador' && (
                      <div className="mb-4">
                        <label className="inline-flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={Boolean(formData.access_contabilidad)}
                            onChange={() => handleModuleAccessToggle('access_contabilidad')}
                            className="h-4 w-4 text-teal-600 rounded border-gray-300 focus:ring-teal-500"
                          />
                          <span className="text-sm text-gray-700">Puede ver el módulo de Contabilidad</span>
                        </label>
                      </div>
                    )}

                    <div className="mb-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Estado <span className="text-red-500">*</span>
                      </label>
                      <select
                        name="is_active"
                        value={formData.is_active ? 'true' : 'false'}
                        onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.value === 'true' }))}
                        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                          formErrors.is_active ? 'border-red-500' : 'border-gray-300'
                        }`}
                        required
                      >
                        <option value="true">Activo</option>
                        <option value="false">Inactivo</option>
                      </select>
                      {formErrors.is_active && errorMsg('is_active') && (
                        <p className="mt-1 text-sm text-red-600">{errorMsg('is_active')}</p>
                      )}
                    </div>
                  </>
                )}

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={handleCloseModal}
                    className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md font-medium transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors"
                  >
                    {selectedUser ? 'Actualizar Rol' : 'Crear'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Restablecer Contraseña */}
      {isResetPasswordModalOpen && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Restablecer contraseña</h2>
              <p className="text-sm text-gray-600 mb-4">
                Usuario: <span className="font-medium">{selectedUser.name}</span> ({selectedUser.email})
              </p>
              <form onSubmit={handleResetPasswordSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nueva contraseña <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    name="password"
                    value={resetPasswordData.password}
                    onChange={handleResetPasswordChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                      resetPasswordErrors.password ? 'border-red-500' : 'border-gray-300'
                    }`}
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                  {resetPasswordErrors.password && (
                    <p className="mt-1 text-sm text-red-600">
                      {Array.isArray(resetPasswordErrors.password) ? resetPasswordErrors.password[0] : resetPasswordErrors.password}
                    </p>
                  )}
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirmar contraseña <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="password"
                    name="password_confirmation"
                    value={resetPasswordData.password_confirmation}
                    onChange={handleResetPasswordChange}
                    className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 ${
                      resetPasswordErrors.password_confirmation ? 'border-red-500' : 'border-gray-300'
                    }`}
                    required
                    minLength={8}
                    autoComplete="new-password"
                  />
                  {resetPasswordErrors.password_confirmation && (
                    <p className="mt-1 text-sm text-red-600">
                      {Array.isArray(resetPasswordErrors.password_confirmation)
                        ? resetPasswordErrors.password_confirmation[0]
                        : resetPasswordErrors.password_confirmation}
                    </p>
                  )}
                </div>
                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={handleCloseResetPasswordModal}
                    className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md font-medium transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md font-medium transition-colors"
                  >
                    Restablecer
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmación de Activar/Desactivar */}
      <ConfirmModal
        isOpen={isToggleStatusModalOpen}
        onClose={() => {
          setIsToggleStatusModalOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={handleToggleStatusConfirm}
        title={selectedUser?.is_active ? "Desactivar Usuario" : "Activar Usuario"}
        message={`¿Estás seguro de que deseas ${selectedUser?.is_active ? 'desactivar' : 'activar'} al usuario "${selectedUser?.name}"?`}
      />

      {/* Toast de notificaciones */}
      <Toast
        isOpen={toast.isOpen}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast({ ...toast, isOpen: false })}
      />
    </div>
  );
};

export default Users;
